import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mail, Lock, User, Store, Loader2, Phone, MessageCircle } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { CURRENCIES, defaultCurrencyForCountry } from "../utils/currency";
import { useEnabledCategoryNames } from "../hooks/useEnabledCategories";
import { CityAutocomplete } from "../components/CityAutocomplete";
import { PasswordField } from "../components/PasswordField";
import { PasswordGenerator } from "../components/PasswordGenerator";
import { useToast } from "../hooks/use-toast";
import { useLang } from "../context/LanguageContext";

const Register = () => {
  const catNames = useEnabledCategoryNames();
  const { t } = useLang();
  const navigate = useNavigate();
  const { register } = useAuth();
  const { toast } = useToast();
  const [f, setF] = useState({ name: "", businessName: "", email: "", password: "", phone: "", whatsapp: "", category: "Food", country: "", location: "", currency: "USD" });
  const [loading, setLoading] = useState(false);
  const [agree, setAgree] = useState(false);
  // Arrived from a printed city sheet: start them in that city.
  const [join] = useState(() => { try { return JSON.parse(localStorage.getItem("lp-join")); } catch { return null; } });
  useEffect(() => {
    if (join?.city) setF((p) => (p.location ? p : { ...p, country: join.country, location: join.city, currency: defaultCurrencyForCountry(join.country) }));
  }, []); // eslint-disable-line
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const setCity = (r) => setF((p) => (r
    ? { ...p, country: r.country, location: r.city, currency: defaultCurrencyForCountry(r.country) }
    : { ...p, country: "", location: "" }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.location || !f.country) {
      toast({ title: t("reg.pickYourCity"), description: t("reg.startTypingYourCity") });
      return;
    }
    setLoading(true);
    try {
      const u = await register({ ...f, acceptTerms: agree, joinCity: join?.key || null });
      localStorage.removeItem("lp-join");
      toast({ title: t("reg.accountCreated"), description: `Welcome, ${u.businessName}` });
      navigate("/account");
    } catch (err) {
      toast({ title: t("reg.registrationFailed"), description: err.response?.data?.detail || "Please try again" });
    } finally { setLoading(false); }
  };

  const input = "w-full bg-transparent outline-none text-sm text-foreground";
  const box = "flex items-center gap-3 border border-border rounded-xl px-4 py-3";
  const sel = "border border-border rounded-xl px-3 py-3 text-sm bg-transparent outline-none text-foreground";

  return (
    <div className="min-h-screen flex items-center justify-center bg-secondary/50 px-5 py-10">
      <div className="w-full max-w-md">
        <Link to="/" className="flex flex-col items-center gap-2 mb-6">
          <img src="/lp-logo.webp" alt="LytePress" className="h-16 w-16 rounded-full border border-border shadow-sm" />
          <span className="text-xl font-extrabold text-foreground">LytePress</span>
        </Link>
        <div className="bg-card border border-border rounded-2xl shadow-xl p-8">
          <h1 className="text-2xl font-extrabold text-foreground text-center mb-1">{t("reg.registerYourBusiness")}</h1>
          <p className="text-sm text-muted-foreground text-center mb-6">{t("reg.startPostingDealsTo")}</p>
          <form onSubmit={submit} className="space-y-4">
            <div className={box}><User size={18} className="text-muted-foreground" /><input required value={f.name} onChange={set("name")} placeholder={t("reg.yourName")} className={input} /></div>
            <div className={box}><Store size={18} className="text-muted-foreground" /><input required value={f.businessName} onChange={set("businessName")} placeholder={t("reg.businessName")} className={input} /></div>
            <div className={box}><Mail size={18} className="text-muted-foreground" /><input type="email" required value={f.email} onChange={set("email")} placeholder="Email" className={input} /></div>
            <PasswordField value={f.password} onChange={set("password")} role="business"
              email={f.email} name={f.name} businessName={f.businessName} testid="reg-password" />
            <PasswordGenerator onPick={(pw) => setF((p) => ({ ...p, password: pw }))} testid="reg-pw-gen" />
            <select value={f.category} onChange={set("category")} className={sel + " w-full"}>{catNames.map((c) => <option key={c} value={c}>{c}</option>)}</select>
            <div className="grid grid-cols-2 gap-3">
              <div className={box}><Phone size={18} className="text-muted-foreground" /><input value={f.phone} onChange={set("phone")} placeholder="Phone" className={input} data-testid="reg-phone" /></div>
              <div className={box}><MessageCircle size={18} className="text-muted-foreground" /><input value={f.whatsapp} onChange={set("whatsapp")} placeholder="WhatsApp" className={input} data-testid="reg-whatsapp" /></div>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground block mb-1 ml-1">{t("reg.whereIsYourBusiness")} <span className="text-muted-foreground/70">(any city worldwide)</span></label>
              <CityAutocomplete city={f.location} country={f.country} onSelect={setCity} className={box} />
              <p className="text-[11px] text-muted-foreground mt-1 ml-1" data-testid="branches-hint">{t("reg.yourMainLocationYou")}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground block mb-1 ml-1">{t("reg.dealCurrency")}</label>
              <select value={f.currency} onChange={set("currency")} data-testid="register-currency-select" className={sel + " w-full"}>{CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}</select>
            </div>
            <label className="flex items-start gap-2.5 cursor-pointer select-none" data-testid="register-terms-label">
              <input type="checkbox" required checked={agree} onChange={(e) => setAgree(e.target.checked)}
                data-testid="register-terms-checkbox"
                className="mt-0.5 h-4 w-4 shrink-0 rounded border-border accent-[#16a34a]" />
              <span className="text-xs text-muted-foreground leading-snug">
                {t("auth.agreeCheckbox")} <Link to="/terms" target="_blank" className="text-[#16a34a] font-semibold hover:underline">{t("reg.termsOfUse")}</Link> {t("reg.and")} <Link to="/privacy" target="_blank" className="text-[#16a34a] font-semibold hover:underline">{t("reg.privacyPolicy")}</Link>.
              </span>
            </label>
            <button disabled={loading || !agree} data-testid="register-submit" className="w-full bg-[#16a34a] hover:bg-[#15803d] disabled:opacity-60 text-white font-semibold py-3 rounded-xl transition-colors flex items-center justify-center gap-2">{loading && <Loader2 className="animate-spin" size={18} />} Create Account</button>
          </form>
          <p className="mt-4 text-center text-sm text-muted-foreground">{t("reg.alreadyRegistered")} <Link to="/login" className="text-[#16a34a] font-semibold hover:underline">{t("reg.logIn")}</Link></p>
        </div>
      </div>
    </div>
  );
};

export default Register;
